import { useState, useEffect, useCallback } from "react";

// First-Time User Experience hook.
//
// Decides whether to auto-open the product tour and persists
// completion so a returning user doesn't see it again.
//
// Storage key shape:
//   `ftue_<role>_<userId>` → "1" once completed/skipped.
//
// Why per-user-id (not just per-role): on a shared device, a
// landlord and a tenant in the same family shouldn't share
// completion state. Without the user id we'd dismiss the tenant's
// first-time tour just because the landlord finished theirs.
//
// API:
//   const { isOpen, complete, skip, replay } = useFtue(role, userId);
//
//   isOpen   — boolean, drives <ProductTour open={isOpen} ... />
//   complete — call from ProductTour onComplete prop
//   skip     — call from ProductTour onSkip prop
//   replay   — programmatically re-open (e.g. from a "Show tour"
//              menu item later). Resets the persisted flag.
export default function useFtue(role, userId) {
  const key = role && userId ? `ftue_${role}_${userId}` : null;
  const [isOpen, setIsOpen] = useState(false);

  // Read persisted state on mount + when role/user changes.
  useEffect(() => {
    if (!key) {
      setIsOpen(false);
      return;
    }
    let stored = null;
    try {
      stored = window.localStorage.getItem(key);
    } catch (_) {
      // localStorage can throw in private mode or with strict
      // browser settings — fail closed (don't show the tour) so
      // we don't pester users on every page load.
      stored = "1";
    }
    // Small delay so the FTUE doesn't fight with the post-login
    // sync loader for screen real estate. By the time the user
    // sees the dashboard, the tour pops in cleanly.
    if (!stored) {
      const id = setTimeout(() => setIsOpen(true), 600);
      return () => clearTimeout(id);
    }
    setIsOpen(false);
  }, [key]);

  const mark = useCallback(() => {
    if (!key) return;
    try { window.localStorage.setItem(key, "1"); } catch (_) { /* ignore */ }
    setIsOpen(false);
  }, [key]);

  const complete = useCallback(() => { mark(); }, [mark]);
  const skip = useCallback(() => { mark(); }, [mark]);

  // Re-open the tour. Clears the persisted flag so the next login
  // would also see it (useful for "I want to walk through this
  // again" via a Settings menu link).
  const replay = useCallback(() => {
    if (!key) return;
    try { window.localStorage.removeItem(key); } catch (_) { /* ignore */ }
    setIsOpen(true);
  }, [key]);

  return { isOpen, complete, skip, replay };
}
